import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { cn } from '@/lib/cn'

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  // `min-w-0` jest tu konieczne, nie kosmetyczne: element siatki ma domyślnie
  // `min-width: auto`, więc szeroka tabela w środku rozpycha kartę ponad
  // szerokość ekranu zamiast uruchomić własne przewijanie. To był powód, dla
  // którego strony z tabelami dawały się „wyjechać poza obrys” na telefonie.
  return <section className={cn('card min-w-0', className)}>{children}</section>
}

export function CardHeader({
  title,
  action,
  className,
}: {
  title: string
  action?: ReactNode
  className?: string
}) {
  return (
    <header
      className={cn(
        'flex items-center justify-between gap-4 border-b border-outline/10 px-6 py-5',
        className,
      )}
    >
      <h2 className="font-headline text-xl font-bold text-secondary">{title}</h2>
      {action}
    </header>
  )
}

type Tone = 'default' | 'primary' | 'error' | 'success' | 'warning'

const TONES: Record<Tone, { card: string; icon: string; value: string }> = {
  default: { card: 'card', icon: 'bg-primary/10 text-primary', value: 'text-on-surface' },
  primary: {
    card: 'card border-l-4 border-l-primary',
    icon: 'bg-surface-container-high text-on-surface',
    value: 'text-on-surface',
  },
  error: {
    card: 'rounded-xl border border-error/20 bg-error-container/20',
    icon: 'bg-error text-on-error',
    value: 'text-on-error-container',
  },
  success: {
    card: 'rounded-xl border border-success/20 bg-success-container/30',
    icon: 'bg-success text-white',
    value: 'text-on-success-container',
  },
  // Sprawa do załatwienia, nie awaria — np. zaległe wpisy obecności.
  warning: {
    card: 'rounded-xl border border-warning/20 bg-warning-container/30',
    icon: 'bg-warning text-on-warning-container',
    value: 'text-on-warning-container',
  },
}

interface StatCardProps {
  label: string
  value: string
  icon: ReactNode
  tone?: Tone
  /** Krótka adnotacja w prawym górnym rogu, np. „+4%” albo „Dzisiaj”. */
  note?: ReactNode
  /**
   * Sekcja, do której prowadzi kafelek.
   *
   * Podawana tylko wtedy, gdy użytkownik faktycznie ma tam wstęp — kafelek
   * prowadzący w „brak dostępu” byłby gorszy od kafelka bez odnośnika.
   */
  to?: string
}

/** Kafelek metryki — układ jak w szablonie panelu głównego. */
export function StatCard({ label, value, icon, tone = 'default', note, to }: StatCardProps) {
  const styles = TONES[tone]

  const tresc = (
    <>
      <div className="flex items-center justify-between">
        <span className={cn('rounded-lg p-3', styles.icon)} aria-hidden="true">
          {icon}
        </span>
        {note}
      </div>
      <div>
        <p className="text-sm font-bold text-on-surface-variant">{label}</p>
        <p className={cn('font-headline text-3xl font-bold', styles.value)}>{value}</p>
      </div>
    </>
  )

  const uklad = cn('flex flex-col gap-4 p-6', styles.card)

  if (to === undefined) return <article className={uklad}>{tresc}</article>

  return (
    <Link
      to={to}
      className={cn(
        uklad,
        // Kafelek jest klikalny w całości, więc musi to po sobie pokazywać —
        // sama liczba nie wygląda na odnośnik.
        'transition-all duration-200 hover:shadow-raised active:scale-[0.99]',
      )}
    >
      {tresc}
    </Link>
  )
}
